import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCartCurrencyStatus } from "@/api/currencyMigration";
import type { CartCurrencyStatus } from "@/types/currencyMigration";

interface UseCartCurrencyStatusResult {
  status: CartCurrencyStatus | null;
  /** Re-check after the member switches, so the warning clears itself. */
  refetch: () => Promise<void>;
}

interface UseCartCurrencyStatusOptions {
  /** Skip the request entirely — e.g. while the cart is still loading or empty. */
  enabled?: boolean;
}

/**
 * Tells the cart whether the member can actually pay for it in its currency.
 *
 * Deliberately has no loading or error surface: this is an early warning, and a
 * cart must never show a spinner or an error because a best-effort check was slow
 * or unavailable. A failed call simply leaves `status` null and the cart silent —
 * the checkout still catches the conflict either way.
 */
export function useCartCurrencyStatus(
  options: UseCartCurrencyStatusOptions = {}
): UseCartCurrencyStatusResult {
  const { enabled = true } = options;
  const [status, setStatus] = useState<CartCurrencyStatus | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const result = await fetchCartCurrencyStatus(controller.signal);
    if (controller.signal.aborted) return;

    setStatus(result.ok ? result.data : null);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
    return () => abortRef.current?.abort();
  }, [load, enabled]);

  return { status, refetch: load };
}
